import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Experiences", href: "experiences" },
  { label: "Programs", href: "programs" },
  { label: "Consultants", href: "consultants" },
  { label: "Packages", href: "packages" },
  { label: "The Venue", href: "venue" },
  { label: "Gallery", href: "gallery" },
  { label: "Contact", href: "contact" },
];

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="main-navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 2.4, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-[80] transition-[background-color,border-color] duration-500 ${
          scrolled ? "bg-forest/85 backdrop-blur-xl border-b border-cream/10" : "border-b border-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="group"
            aria-label="LUSH Adventure — back to top"
          >
            <span className="inline-block bg-cream px-3 py-2 rounded-sm shadow-lg shadow-black/30 group-hover:shadow-amber/20 transition-shadow duration-300">
              <img src="/logo.png" alt="LUSH Adventure @ Barn logo" className="h-10 md:h-12 w-auto" />
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-10" data-testid="nav-links">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.href}`}
                onClick={() => scrollTo(l.href)}
                className="text-xs tracking-[0.2em] uppercase text-cream/70 hover:text-amber transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-cta-plan-day"
              onClick={() => scrollTo("contact")}
              className="border border-amber/60 text-amber text-xs tracking-[0.2em] uppercase px-6 py-3 hover:bg-amber hover:text-night transition-colors duration-300"
            >
              Plan Your Day
            </button>
          </nav>

          <button
            data-testid="nav-mobile-menu-button"
            className="lg:hidden text-cream"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-night/97 backdrop-blur-xl flex flex-col"
          >
            <div className="flex justify-between items-center p-6">
              <span className="inline-block bg-cream px-3 py-1.5 rounded-sm">
                <img src="/logo.png" alt="LUSH Adventure @ Barn logo" className="h-8 w-auto" />
              </span>
              <button
                data-testid="nav-mobile-close-button"
                onClick={() => setOpen(false)}
                className="text-cream"
                aria-label="Close menu"
              >
                <X size={30} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col items-start justify-center px-10 gap-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.href}`}
                  initial={{ x: -40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => {
                    setOpen(false);
                    setTimeout(() => scrollTo(l.href), 250);
                  }}
                  className="font-display text-4xl text-cream hover:text-amber transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
